//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle
} from '@mui/material';

import {
  UnsavedChangesContext, type UnsavedChangesContextValue
} from './UnsavedChangesContext';

interface UnsavedChangesProviderProps {
  children: ReactNode;
}

/**
 * Which parts of the Settings modal hold unsaved edits, and the one question
 * asked before an action would unmount them: a pane switch, a close, or a
 * pane replacing one of its own editors.
 */
const UnsavedChangesProvider = ({ children }: UnsavedChangesProviderProps) => {
  const [owners, setOwners] = useState<ReadonlySet<string>>(new Set());
  // The action waiting on the user's word to drop the edits.
  const [pending, setPending] = useState<(() => void) | null>(null);

  const report = useCallback((owner: string, unsaved: boolean) =>
    setOwners((prev) => {
      if (prev.has(owner) === unsaved) return prev;
      const next = new Set(prev);
      if (unsaved) next.add(owner); else next.delete(owner);
      return next;
    }), []);

  const hasUnsaved = owners.size > 0;
  const confirmDiscard = useCallback((proceed: () => void) => {
    if (hasUnsaved) setPending(() => proceed);
    else proceed();
  }, [hasUnsaved]);

  const value = useMemo<UnsavedChangesContextValue>(
    () => ({ report, confirmDiscard }), [report, confirmDiscard]);

  const close = () => setPending(null);

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <Dialog open={pending !== null} onClose={close}
          aria-labelledby="discard-unsaved-changes-title"
          aria-describedby="discard-unsaved-changes-warning">
        <DialogTitle id="discard-unsaved-changes-title">
          Discard unsaved changes?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="discard-unsaved-changes-warning">
            Changes on this page have not been saved.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={close}>Keep editing</Button>
          <Button variant="contained" color="error"
              onClick={() => {
                pending?.();
                close();
              }}>
            Discard changes
          </Button>
        </DialogActions>
      </Dialog>
    </UnsavedChangesContext.Provider>
  );
};

export default UnsavedChangesProvider;
